/**
 * View-toggle telemetry — local-only 2D↔3D usage signal (PR N).
 *
 * Atlas is internal-only and we don't have a backend analytics sink for
 * this. The cheap-and-sufficient option: append events to a bounded
 * localStorage queue, expose a `summariseToggles()` helper for ad-hoc
 * console inspection. Pure functions; no fetch, no side effects beyond
 * the storage write.
 *
 * Read from the browser console:
 *
 *     import('/src/state/viewToggleTelemetry.ts').then(m => {
 *       console.table(m.readToggleEvents());
 *       console.log(m.summariseToggles(m.readToggleEvents()));
 *     });
 */
import type { ViewMode } from './store.js';

export interface ViewToggleEvent {
  from: ViewMode;
  to: ViewMode;
  /** ms spent in the `from` view before this toggle. */
  durationInPrevMs: number;
  /** Wall-clock timestamp when the toggle fired. */
  at: number;
}

export const TOGGLE_QUEUE_CAP = 200;
const KEY = 'atlas.gl3d.toggles';

function readRaw(): ViewToggleEvent[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is ViewToggleEvent =>
        typeof e === 'object' &&
        e !== null &&
        (e as ViewToggleEvent).from !== undefined &&
        (e as ViewToggleEvent).to !== undefined &&
        typeof (e as ViewToggleEvent).at === 'number',
    );
  } catch {
    return [];
  }
}

function writeRaw(events: ViewToggleEvent[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(events));
  } catch {
    /* hostile env — silent. */
  }
}

export function recordViewToggle(event: ViewToggleEvent): void {
  const current = readRaw();
  const next = [...current, event];
  const capped = next.length > TOGGLE_QUEUE_CAP ? next.slice(next.length - TOGGLE_QUEUE_CAP) : next;
  writeRaw(capped);
}

export function readToggleEvents(): ViewToggleEvent[] {
  return readRaw();
}

export function clearToggleEvents(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* silent */
  }
}

export interface ToggleSummary {
  total: number;
  to3d: number;
  to2d: number;
  avgDurationMs: number;
}

export function summariseToggles(events: readonly ViewToggleEvent[]): ToggleSummary {
  if (events.length === 0) {
    return { total: 0, to3d: 0, to2d: 0, avgDurationMs: 0 };
  }
  let to3d = 0;
  let to2d = 0;
  let total = 0;
  let totalDuration = 0;
  for (const e of events) {
    total++;
    totalDuration += e.durationInPrevMs;
    if (e.to === '3d') to3d++;
    else if (e.to === '2d') to2d++;
  }
  return {
    total,
    to3d,
    to2d,
    avgDurationMs: totalDuration / total,
  };
}
